<template>
  <div class="aplaza-export-component">
    <a-form v-bind:form="form" ref="form" method="POST" target="_blank" action="#">
      <input type="hidden" name="_token" v-bind:value="csrfToken"/>
      <div class="panel panel-stretch">
        <div class="panel-heading">
          Exporteer Klantgegevens
        </div>
        <div class="panel-body">
          Exporteer klant en polis data naar een bij Aplaza aangesloten partij. De website van de aangesloten partij
          zal automatisch worden geopend.

          <a-row>
            <a-col v-bind:span="12">
              <a-form-item label="Exporteren naar"
                           v-bind:rules="[{
                             required: true,
                             message: 'Dit veld is verplicht',
                           }]">
                <a-select
                    v-decorator="[
                        'providerId',
                        {
                          initialValue: selectedProviderId,
                          rules: [{
                            required: true,
                            message: 'Dit veld is verplicht',
                          }],
                        }
                      ]"
                    placeholder="Selecteer een partij"
                    v-on:change="onProviderChange"
                    v-bind:loading="loadingProviders"
                >
                  <a-select-option v-for="provider in providers"
                                   v-bind:key="provider.id" v-bind:value="provider.id">
                    {{ provider.product_name }} ({{ provider.export_type_description }})
                  </a-select-option>
                </a-select>
              </a-form-item>
              <input type="hidden" name="providerId" v-bind:value="selectedProviderId"/>

              <template v-if="isPolicyProvider">
                <a-form-item label="Polis">
                  <a-select
                      v-decorator="[
                        'policyId',
                        {
                          initialValue: selectedPolicyId,
                          rules: [{
                            required: true,
                            message: 'Dit veld is verplicht',
                          }],
                        }
                      ]"
                      placeholder="Selecteer een polis"
                      v-on:change="onPolicyChange"
                      v-bind:loading="loadingPolicies"
                  >
                    <a-select-option v-for="policy in policies"
                                     v-bind:key="policy.id" v-bind:value="policy.id">
                      <div class="policy-option">
                        <p v-if="policy.name !== '' && policy.name !== null">
                          <b>{{ policy.name }}</b>
                        </p>
                        <div v-html="sanitizeHTML(policy.description)"></div>
                      </div>
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <input type="hidden" name="policyId" v-bind:value="selectedPolicyId"/>
              </template>

              <template v-if="isDamageProvider">
                <a-form-item label="Schade">
                  <a-select
                      v-decorator="[
                        'damageId',
                        {
                          initialValue: selectedDamageId,
                          rules: [{
                            required: true,
                            message: 'Dit veld is verplicht',
                          }],
                        }
                      ]"
                      placeholder="Selecteer een schade"
                      v-on:change="onDamageChange"
                      v-bind:loading="loadingDamages"
                  >
                    <a-select-option v-for="damage in damages"
                                     v-bind:key="damage.id" v-bind:value="damage.id">
                      <div>
                        <license-plate v-if="damage.licenseplate"
                                       v-bind:license-plate="damage.licenseplate"
                                       v-bind:inside-select="true"/>
                        <b v-if="damage.damage_number !== '' && damage.damage_number !== null">
                          Schadenummer {{ damage.damage_number }}
                        </b>
                      </div>
                      Polis {{ damage.policy_contract_number }}
                      Schadedatum {{ formatDate(damage.date) }}
                      <div v-html="sanitizeHTML(damage.description)"></div>
                    </a-select-option>
                  </a-select>
                </a-form-item>
                <input type="hidden" name="damageId" v-bind:value="selectedDamageId"/>
              </template>

              <a-form-item label="Aplaza account"
                           v-bind:rules="[{
                             required: true,
                             message: 'Dit veld is verplicht',
                           }]">
                <a-select
                    v-decorator="[
                        'userId',
                        {
                          initialValue: selectedUserId,
                          rules: [{
                            required: true,
                            message: 'Dit veld is verplicht',
                          }],
                        }
                      ]"
                    placeholder="Selecteer een account"
                    v-on:change="onUserChange"
                    v-bind:loading="loadingUsers"
                >
                  <a-select-option v-for="user in users"
                                   v-bind:key="user.id" v-bind:value="user.id">
                    {{ user.display_name }}
                  </a-select-option>
                </a-select>
                <small>De Aplaza account waarmee de gegevens worden verzonden.</small>
              </a-form-item>
              <input type="hidden" name="userId" v-bind:value="selectedUserId"/>
            </a-col>
          </a-row>
        </div>
        <div class="panel-footer text-right">
          <a-button type="primary" v-on:click="submit">
            Verzenden
          </a-button>
        </div>
      </div>
    </a-form>
  </div>
</template>

<script>

import aplazaRegisterApi from '~/api/aplazaRegister';
import aplazaUserApi from '~/api/aplazaUser';
import damageApi from '~/api/damage';
import policyApi from '~/api/policy';
import AplazaExportTypeEnum from "~/enums/AplazaExportTypeEnum";
import moment from 'moment';
import DOMPurify from "dompurify";

export default {
  name: "AplazaExportComponent",
  props: {
    customerId: {
      required: true
    }
  },
  components: {},
  data() {
    return {
      customer: null,
      selectedProviderId: undefined,
      providers: [],
      loadingProviders: true,
      selectedPolicyId: undefined,
      policies: [],
      loadingPolicies: true,
      selectedDamageId: undefined,
      damages: [],
      loadingDamages: true,
      selectedUserId: undefined,
      users: [],
      loadingUsers: true,
      form: this.$form.createForm(this),
      moment,
    }
  },
  computed: {
    selectedProvider: function () {
      let filteredProviders = this.providers.filter(provider => provider.id === this.selectedProviderId);

      return filteredProviders.length === 1 ? filteredProviders[0] : null;
    },
    isPolicyProvider: function () {
      return this.selectedProvider !== null && this.selectedProvider.export_type === AplazaExportTypeEnum.POLICY;
    },
    isDamageProvider: function () {
      return this.selectedProvider !== null && this.selectedProvider.export_type === AplazaExportTypeEnum.DAMAGE;
    },
    csrfToken: function () {
      return window.state.csrf_token;
    }
  },
  mounted() {
    this.initConnectProviders();
    this.initAplazaUsers();
  },
  methods: {
    onProviderChange(providerId) {
      this.selectedProviderId = providerId;

      if (this.isPolicyProvider) {
        this.initPolicies();
      }
      if (this.isDamageProvider) {
        this.initDamages();
      }
    },
    onPolicyChange(policyId) {
      this.selectedPolicyId = policyId;
    },
    onDamageChange(damageId) {
      this.selectedDamageId = damageId;
    },
    onUserChange(userId) {
      this.selectedUserId = userId;
    },
    initConnectProviders() {
      return aplazaRegisterApi.getConnectProviders().then((response) => {
        this.providers = response.data;
        this.loadingProviders = false;
      })
    },
    initAplazaUsers() {
      return aplazaUserApi.getAll().then((response) => {
        this.users = response.data;

        if (this.users.length > 0) {
          this.selectedUserId = this.users[0].id;
        }

        this.loadingUsers = false;
      })
    },
    initPolicies() {
      if (this.policies.length === 0) {
        return policyApi.getByCustomerId(this.customerId).then(response => {
          this.policies = response.data;
          this.loadingPolicies = false;
        })
      }
    },
    initDamages() {
      if (this.damages.length === 0) {
        return damageApi.getByCustomerId(this.customerId).then(response => {
          this.damages = response.data;
          this.loadingDamages = false;
        })
      }
    },
    formatDate(date) {
      if (date !== null && typeof date !== 'undefined' && date !== '') {
        let moment = this.moment(date, this.moment.ISO_8601);
        return moment.isValid() ? moment.format('DD/MM/YYYY') : date;
      }

      return date;
    },
    submit() {
      this.form.validateFields((errors, data) => {
        if (errors === null) {
          this.$refs.form.$el.submit();
        }
      });
    },
    sanitizeHTML(html) {
      return DOMPurify.sanitize(html, {ADD_TAGS: ['kenteken']});
    }
  }
}
</script>

<style module>
.policy-option kenteken {
  overflow: hidden;
  display: inline-block;
  white-space: nowrap;
  vertical-align: middle;
  line-height: 15px;
  margin-top: -4px;
}

.policy-option kenteken img {
  background-color: #2463A8;
  display: inline-block;
  height: 21px;
  width: 14px;
  padding: 3px;
  vertical-align: bottom;
}

.policy-option kenteken span {
  text-transform: uppercase;
  background-color: #F5BD04;
  font-family: "Kenteken";
  font-size: 12px;
  padding: 3px 5px;
  color: #2d2c2c;
  display: inline-block;
}

.aplaza-export-component .ant-select-selection.ant-select-selection--single {
  overflow: hidden;
}
</style>
